import type { ReactNode } from 'react';
import { CHROME, CHROME_LAYOUT } from './registry';

/**
 * The chrome family: controls that narrow or move what is on screen.
 *
 * These are the controls the operator's acceptance test names — "make the filter bar
 * purple and every filter and search control changes". They all read their paint from the
 * `--ctl-*` custom properties, so that change is one edit in `styles/global.css` and
 * reaches every component in this file at once.
 *
 * None of these accepts `className`. See the note in `Button.tsx`.
 */

export interface FilterOption<T extends string> {
  id: T;
  label: string;
  /**
   * A word about the option itself — "override", "off".
   *
   * DATA, NOT DECORATION. It answers a question the label cannot: which of these projects
   * carries a setting of its own? Without it a reader has to click every option to find out,
   * which is the same work the group exists to save them.
   */
  marker?: string;
}

export interface FilterBarProps<T extends string> {
  /** Names the group for a screen reader. Visually hidden; the pills carry the visible text. */
  legend: string;
  options: readonly FilterOption<T>[];
  selected: T;
  onSelect: (id: T) => void;
}

/**
 * A row of filter pills. A `fieldset` rather than a `div` so the group has one accessible
 * name — without it a screen reader reads seven unlabelled toggles in a row.
 */
export function FilterBar<T extends string>({
  legend,
  options,
  selected,
  onSelect,
}: FilterBarProps<T>) {
  return (
    <fieldset className={CHROME_LAYOUT.filters}>
      <legend className="vh">{legend}</legend>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={CHROME.filter}
          aria-pressed={selected === option.id}
          onClick={() => onSelect(option.id)}
        >
          {selected === option.id ? <PressedMark /> : null}
          {option.label}
          {/* Inside the button so it joins the accessible name: "Modero, override" is one
              option. A marker announced separately would read as a stray word. */}
          {option.marker === undefined ? null : <span className="chip ml-2">{option.marker}</span>}
        </button>
      ))}
    </fieldset>
  );
}

export interface ToggleGroupProps<T extends string> {
  legend: string;
  options: readonly FilterOption<T>[];
  selected: T;
  onSelect: (id: T) => void;
  /**
   * The group sits inside a row that already spaces itself — a panel header, beside the
   * heading. Without it the group keeps the trailing margin it needs when it stands alone
   * above a list, and in a bottom-aligned header that margin lifts it out of line with the
   * heading it belongs to.
   */
  flush?: boolean;
}

/**
 * An exclusive view switch. Same family as `FilterBar` and deliberately the same paint —
 * a reader does not distinguish "narrow this" from "show it differently", and giving them
 * separate vocabularies is how the stylesheet ended up with `.toggle` and `.fbtn` drifting
 * apart at different heights.
 */
export function ToggleGroup<T extends string>({
  legend,
  options,
  selected,
  onSelect,
  flush = false,
}: ToggleGroupProps<T>) {
  return (
    <fieldset className={flush ? CHROME_LAYOUT.filtersFlush : CHROME_LAYOUT.filters}>
      <legend className="vh">{legend}</legend>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={CHROME.toggle}
          aria-pressed={selected === option.id}
          onClick={() => onSelect(option.id)}
        >
          {selected === option.id ? <PressedMark /> : null}
          {option.label}
          {/* Inside the button so it joins the accessible name: "Modero, override" is one
              option. A marker announced separately would read as a stray word. */}
          {option.marker === undefined ? null : <span className="chip ml-2">{option.marker}</span>}
        </button>
      ))}
    </fieldset>
  );
}

/** A horizontal bar for a search box and whatever sits beside it. */
export function ControlBar({ children }: { children: ReactNode }) {
  return <div className={CHROME_LAYOUT.bar}>{children}</div>;
}

/** A row of action buttons. */
export function ButtonRow({ children }: { children: ReactNode }) {
  return <div className={CHROME_LAYOUT.buttonRow}>{children}</div>;
}

export interface PagerProps {
  onPrevious: () => void;
  onNext: () => void;
  canPrevious: boolean;
  canNext: boolean;
  /** Read aloud and shown: "Page 2 of 9". Never a bare pair of arrows. */
  label: string;
}

/**
 * Pagination.
 *
 * Its buttons were previously styled by `.pager button` — a rule keyed on DOM position, so
 * any control placed inside the pager inherited a look nobody asked for and no primitive
 * could override. The class is now on the button itself.
 */
export function Pager({ onPrevious, onNext, canPrevious, canNext, label }: PagerProps) {
  return (
    <nav className={CHROME_LAYOUT.pager} aria-label="Pagination">
      <button type="button" className={CHROME.pager} onClick={onPrevious} disabled={!canPrevious}>
        Previous
      </button>
      <span>{label}</span>
      <button type="button" className={CHROME.pager} onClick={onNext} disabled={!canNext}>
        Next
      </button>
    </nav>
  );
}

/**
 * The check in a pressed option. Hidden from assistive technology, which already hears
 * `aria-pressed`; it is there for the eye, so "pressed" is not carried by hue alone.
 */
function PressedMark() {
  return (
    <svg
      className={CHROME.pressedMark}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
